import { useEffect, useMemo } from "react";
import { useLocation } from "react-router-dom";
import { getSEOServiceBySlug } from "@/data/seoPageData";

type Crumb = { name: string; path: string };

const SITE_ORIGIN = "https://lazonarestorative.com";

function titleFromSlug(slug: string) {
  return slug
    .replace(/-az$/, "")
    .replace(/-/g, " ")
    .replace(/\b\w/g, (c) => c.toUpperCase());
}

export function BreadcrumbSchema() {
  const location = useLocation();

  const crumbs = useMemo<Crumb[]>(() => {
    const pathname = location.pathname || "/";
    if (pathname === "/") return [];

    const slug = pathname.replace(/^\//, "");
    const segments = slug.split("/").filter(Boolean);

    // SEO service pages
    const seoService = getSEOServiceBySlug(segments[0] || "");
    if (segments.length === 1 && seoService) {
      return [
        { name: "Home", path: "/" },
        { name: "Services", path: "/services" },
        { name: seoService.title, path: `/${seoService.slug}` },
      ];
    }

    // Core routes
    if (segments[0] === "services") {
      if (segments.length === 1) {
        return [
          { name: "Home", path: "/" },
          { name: "Services", path: "/services" },
        ];
      }
      return [
        { name: "Home", path: "/" },
        { name: "Services", path: "/services" },
        { name: titleFromSlug(segments[1] || ""), path: pathname },
      ];
    }

    if (segments[0] === "commercial") {
      if (segments.length === 1) {
        return [
          { name: "Home", path: "/" },
          { name: "Commercial", path: "/commercial" },
        ];
      }
      return [
        { name: "Home", path: "/" },
        { name: "Commercial", path: "/commercial" },
        { name: titleFromSlug(segments[1] || ""), path: pathname },
      ];
    }

    // Generic fallback: Home + each segment
    const fallback: Crumb[] = [{ name: "Home", path: "/" }];
    let accPath = "";
    segments.forEach((seg) => {
      accPath += `/${seg}`;
      fallback.push({ name: titleFromSlug(seg), path: accPath });
    });
    return fallback;
  }, [location.pathname]);

  useEffect(() => {
    const existing = document.getElementById("breadcrumb-schema");
    if (existing) existing.remove();

    if (!crumbs.length) return;

    const schema = {
      "@context": "https://schema.org",
      "@type": "BreadcrumbList",
      itemListElement: crumbs.map((c, idx) => ({
        "@type": "ListItem",
        position: idx + 1,
        name: c.name,
        item: `${SITE_ORIGIN}${c.path === "/" ? "" : c.path}`,
      })),
    };

    const script = document.createElement("script");
    script.type = "application/ld+json";
    script.id = "breadcrumb-schema";
    script.textContent = JSON.stringify(schema);
    document.head.appendChild(script);

    return () => {
      const s = document.getElementById("breadcrumb-schema");
      if (s) s.remove();
    };
  }, [crumbs]);

  return null;
}
